import { useRef, useCallback } from 'react';
import { NestedHome } from '@habity/types';
import { getHomePath } from '@habity/services';
import { useUser } from './useUser';
import { useUIStore } from '~/store/useUIStore';

/**
 * Opens the EnhancedLoginModal (non-dismissable) when a logged-out user
 * scrolls past the sentinel element on a show_public_preview home profile.
 *
 * Returns a callback ref to attach to an invisible sentinel div.
 * Uses a callback ref so the IntersectionObserver is created when the
 * element mounts (works for elements inside modals that render lazily).
 */
export const usePublicPreviewGate = (home: NestedHome) => {
  const { user } = useUser();
  const hasTriggered = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const setModals = useUIStore((state) => state.setModals);

  const shouldGate = !user && home?.status?.show_public_preview === true;

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node || !shouldGate || hasTriggered.current) return;

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasTriggered.current) {
            hasTriggered.current = true;
            observerRef.current?.disconnect();

            const modals = useUIStore.getState().modals;
            setModals({
              ...modals,
              enhancedLoginOpen: {
                isOpen: true,
                home,
                project: null,
                action: 'clicked_public_preview_home',
                redirectTo: getHomePath(home),
                preventClose: true,
              },
            });
          }
        },
        { threshold: 0 }
      );

      observerRef.current.observe(node);
    },
    [shouldGate, home, setModals]
  );

  return { sentinelRef };
};
